/**
 * This service is adapted from the angular.io's navigation service (v10.x)
 * https://github.com/angular/angular/blob/10.0.x/aio/src/app/shared/location.service.ts
 *
 * Overhauled Address List does not use this service at all -
 * syncing filters with URI can be done inside an NGRX effect by calling router.navigate()
 * relative to active route (so there is no page refresh) and passing filter values as query params
 * .navigate() will then use standard encoding - if there is need for custom encoding/decoding =>
 * use a utility function or do it directly in the effect body. Query params can be easily accessed
 * by selecting the global NGRX router store.
 * urlSubject, go(), goExternal() and their helper methods are never used anywhere in the APP.
 *
 * TODO: It would make more sense to convert this service into just a UTILS file containing custom
 *  query param(s) encoding/decoding functions, keep (adapt) parseUrl(), setUrlParameters()
 *  since they are also being used by other modules until NGRX is used across the entire app.
 *  Everything else can be deleted
 */
import { Injectable } from '@angular/core';
import { Location, PlatformLocation } from '@angular/common';

import { ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { parseISO } from 'date-fns';
import { NavigationExtras, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  /*
  If you initialize a ReplaySubject with a buffer size of 1,
  then it actually behaves just like a BehaviorSubject. The last value is always cached,
  so it acts like a value changing over time. With this, there is no need
  for a null check like in the case of the BehaviorSubject initialized with a null,
  because no value is ever emitted to the subscriber until the first publishing.
  */
  private urlSubject = new ReplaySubject<string>(1);

  currentUrl = this.urlSubject.pipe(map(url => NavigationService.stripSlashes(url)));

  currentPath = this.currentUrl.pipe(
      map(url => (url.match(/[^?#]*/) || [])[0]), // strip query and hash,
  );

  constructor(
      private location: Location,
      private platformLocation: PlatformLocation,
      private router: Router,
  ) {
    this.urlSubject.next(location.path(true));

    this.location.subscribe(state => {
      return this.urlSubject.next(state.url || '');
    });
  }

  go(url: string | null | undefined) {
    if (!url) {
      return;
    }
    url = NavigationService.stripSlashes(url);
    if (/^http/.test(url)) {
      // Has http protocol so leave the site
      this.goExternal(url);
    } else {
      this.location.go(url);
      this.urlSubject.next(url);
    }
  }

  goExternal(url: string) {
    window.location.assign(url);
  }

  /**
   * Navigation service urls should never start with a slash because those could be understood as
   * external URLs.
   * Also, there shouldn't be a slash between the path and the query/hash part.
   * Introduced in https://github.com/angular/angular/commit/9c1318d7315027ebd67d112bf827a08a64ca7e26
   * @param url
   * @private
   */
  private static stripSlashes(url: string) {
    return url
        /**
         * Remove the leading slash -> "/some/route" --> "some/route"
         * ---
         * Reasoning:
         There is a weirdness in the Angular Location service.
         If the `baseHref` is only a single slash (`'/'`) then it
         changes it to be an empty string (`''`). The effect of this
         is that `Location.normaliseUrl(url)` does not strip off the
         leading slash from url paths.

         The problem is that the leading slash only appears on the
         initial Location path, and not on urls that arrive from subscribing
         to the Location service.
         Introduced in https://github.com/angular/angular/commit/fe962f6de7d65fa6fc62c6b22c9de3a60dfb7358
         */
        .replace(/^\/+/, '')
        /**
         * Remove the last trailing slash. Two edge cases:
         *
         *   With a query and/or a hash:
         *     from https://google.com/path/?abc=def
         *     to   https://google.com/path?abc=def
         *
         *   and without a query
         *
         *     from https://google.com/path/
         *     to   https://google.com/path
         */
        .replace(/\/+(\?|#|$)/, '$1');
  }

  /**
   * Parse and deserialize the url PATH into a dictionary
   * @param path_
   * @param options
   */
  parseUrl(path_?: string,
           options?: {
             arrayFilterKeys?: string[], // specify filters that expect their parsed params to be an array
             convertBooleans?: boolean, // convert 'true' / 'false' to booleans
             convertDates?: boolean,
           },
  ) {
    const search: {
      [index: string]: string | boolean | string[] | Date | undefined;
    } = {};

    const path = path_ || this.location.path();
    const q = path.indexOf('?');
    const arrayFilterKeys = options?.arrayFilterKeys;
    const convertBooleans = typeof options?.convertBooleans === 'boolean' ? options.convertBooleans : true;
    const convertDates = typeof options?.convertDates === 'boolean'
        ? options.convertDates
        // default to false unless overridden
        : false;

    if (q > -1) {
      try {
        const params = path.substr(q + 1).split('&');
        params.forEach(p => {
          const pair = p.split('=');

          if (pair?.length === 2) {
            const [key, value] = pair;
            const pre_parsed = value && decodeURIComponent(value);
            let parsed: typeof search[keyof typeof search];

            if (arrayFilterKeys.includes(key)) {
              // convert to array
              parsed = pre_parsed.split(',');
            } else if (convertBooleans && ['true', 'false'].includes(pre_parsed.toLowerCase())) {
              // convert to boolean
              parsed = pre_parsed.toLowerCase() === 'true';
            } else if (convertDates && key.toLowerCase().includes('date')) {
              // no date validation for now
              parsed = pre_parsed ? parseISO(pre_parsed) : '';
            } else {
              parsed = pre_parsed;
            }

            search[decodeURIComponent(key)] = parsed;
          }
        });
      } catch (e) {
        /* don't care */
      }
    }
    return search;
  }

  /**
   * Use the `.replaceState` strategy to update the URL params
   * and ensure proper URL serialization while you're at it.
   * @param params
   * @param options
   */
  setUrlParameters(
      params:
          | {
        [key: string]:
            | string
            | boolean
            | undefined
            | Map<string, string> // e.g. sortMap
            | string[] // e.g. storeCode
            | number; // e.g. page or pageSize
      },
      options?: {
        label?: string,
        arrayFilterKeys?: string[] // specify filters that expect their parsed params to be an array
      },
  ) {
    const label = options?.label || 'Business Location Services';
    const arrayFilterKeys = options?.arrayFilterKeys;
    const search = Object.keys(params).reduce((acc, key) => {
      let value = params[key];

      if (value === undefined || value === null) {
        return acc;
      }

      // skip empty arrays
      if (value instanceof Array && !value.length) {
        return acc;
      }

      // skip emptyish strings
      if (typeof value === 'string' && (!value.length || value === 'any')) {
        return acc;
      }

      // handle sortMap object
      if (value instanceof Map) {
        if (value.size === 0) {
          return acc;
        }
        value = JSON.stringify(Array.from(value.entries()));
      }

      // need to json stringify to keep the brackets
      // for an easy way to parse
      if (value instanceof Array && arrayFilterKeys.includes(key)) {
        value = value.join(',');
      }

      const to_append =
          (acc ? '&' : '?') +
          `${key}=${value}`;

      return acc + to_append;
    }, '');

    this.platformLocation.replaceState(
        {},
        label,
        encodeURI(this.platformLocation.pathname + search),
    );
  }

  /**
   * Navigate based on the provided array of commands and a starting point. If no starting route is
   * provided, the navigation is absolute.
   * navigate(['team', 33, 'user', 11], {relativeTo: route});
   *
   * Navigate without updating the URL, overriding the default behavior
   * navigate(['team', 33, 'user', 11], {relativeTo: route, skipLocationChange: true});
   *
   * @param commands
   * @param extras
   */
  navigate(commands: any[], extras: NavigationExtras = {}) {
    return this.router.navigate(commands, extras);
  }
}
